/**
 * The invariant on presence hours (#654).
 *
 * `effectiveHours` is the most critical piece of data in the system: it feeds the report cards,
 * the absence reports, the sanctions and Charlemagne. Nothing validated it — neither the dialog
 * nor the server (`PresenceService` stores it as is). This module carries the rule, in a single
 * place, for the two recording surfaces that apply it (module-selector-dialog.vue and
 * assiduite-cell-panel.vue — see §1.7).
 *
 * The settled rule (§2): we **warn, we never block**. Legitimate gaps exist (shortened period,
 * activity outside a module) and a hard block would push towards workarounds worse than the gap
 * itself — not to mention the offline recording queue, which must be able to replay what was
 * recorded in the field.
 */

// `formatHours` moved to `utils/format.ts` (#739, the single source of display formatting);
// it is still used here by `describeHoursDiscrepancy` to compose its message.
import { translate } from '@fondationcss/sdk';
import { formatHours } from '@/utils/format';

/** Any row carrying hours — the minimal shape this module manipulates. */
export interface HoursBearingRow {
    effectiveHours: number;
    /** True as soon as the user has adjusted the duration by hand: never overwritten again (§1.4). */
    touched?: boolean;
    /** Present = the row comes from a presence already in the database; its hours are a fact, not a default. */
    presenceId?: string;
}

/**
 * Tolerated gap, in hours. Absorbs the rounding to two decimals without crying wolf (§2.4):
 * a conservative split lands exactly, but a manual entry to the quarter hour on a period of
 * 3.75 h must not show a gap over one hundredth.
 */
export const HOURS_TOLERANCE = 0.01;

/** Rounded to two decimals — the storage unit of effective hours. */
export function roundHours(hours: number): number {
    return Math.round(hours * 100) / 100;
}

export interface HoursTargetInput {
    /** Planned duration of the period, in hours. 0 when it is unknown. */
    periodHours: number;
    /**
     * Late / early-departure minutes, when the status captures them. They are subtracted from
     * the target: a student who arrived 30 min late on a 7.5 h period did not work 7.5 h of
     * modules (§2.1). Until now `lateEarlyMinutes` and `effectiveHours` lived in two separate
     * worlds — that was the missing link.
     */
    lateEarlyMinutes?: number | null;
}

/**
 * The target: what the sum of the durations should be worth.
 *
 * On an absence status, the target stays the whole period: the hours recorded under
 * « Absence non-motivée » *are* the absence hours, counted as such by `AbsenceReportService`.
 * Forcing them to 0 would empty the absence report of its substance (§2.2). Those statuses
 * capture no minutes, so the computation below already leaves them whole.
 */
export function computeHoursTarget({ periodHours, lateEarlyMinutes }: HoursTargetInput): number {
    const deducted = (lateEarlyMinutes ?? 0) / 60;
    return roundHours(Math.max(0, periodHours - deducted));
}

/**
 * The ceiling a single presence’s duration cannot cross (#997). A day, because a presence is
 * anchored to one period of one date — nothing about a shortened period, a doubled one or a long
 * exam can exceed it. Deliberately loose: the tight bound (the period’s own duration) would
 * reverse #654 §2 and make older over-period rows unsavable on re-edit.
 */
export const MAX_PRESENCE_HOURS = 24;

/**
 * Why a duration field refuses its value (#997) — empty string when nothing is wrong.
 *
 * NOT a bound against the period. The settled rule of #654 §2 is that we warn and never block on
 * the gap between the recorded total and the period: legitimate gaps exist, and a hard block
 * would push towards workarounds worse than the gap. This is the other thing — a value that is
 * not a gap but an impossibility. A presence covers one period of one day, so a duration below
 * zero or above twenty-four hours cannot be what anyone meant; a QA session reached 999 h on a
 * 3,5 h period by typing it, and it flowed on into the module’s actual hours, the absence
 * reports, the teacher workload and the figures that leave for Charlemagne.
 *
 * The warning band between the two is untouched: over the period and under a day still saves,
 * and still says so out loud.
 */
export function hoursInputError(raw: number | string | null | undefined): string {
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return '';
    if (raw < 0) return translate('parcours.presence.hoursNegative');
    if (raw > MAX_PRESENCE_HOURS) return translate('parcours.presence.hoursExceedMax', { max: MAX_PRESENCE_HOURS });
    return '';
}

/**
 * Why a minutes field refuses its value (#995) — empty string when nothing is wrong.
 *
 * These minutes are a duration, and {@link computeHoursTarget} SUBTRACTS them from the period, so
 * a negative one inflates the target instead of reducing it; the dialogs' auto-distribution then
 * writes more effective hours than the period lasts. `min="0"` on the inputs is a native hint no
 * code reads, so the three screens that capture minutes state the rule through this instead. The
 * server refuses the same value, which is what actually closes the hole.
 */
export function minutesInputError(raw: number | string | null | undefined): string {
    return typeof raw === 'number' && raw < 0 ? translate('parcours.presence.minutesNegative') : '';
}

/** `unknown` = no usable target (unknown period duration): we assert nothing. */
export type HoursState = 'ok' | 'over' | 'under' | 'unknown';

export interface HoursSummary {
    /** Sum of the recorded durations. */
    total: number;
    target: number;
    /** `total - target`. Negative = hours are missing. */
    delta: number;
    state: HoursState;
    /** Share of the target covered, clamped to [0, 1] — for the progress bar. */
    ratio: number;
}

export function summarizeHours(rows: readonly HoursBearingRow[], target: number): HoursSummary {
    const total = roundHours(rows.reduce((sum, row) => sum + (Number(row.effectiveHours) || 0), 0));
    const delta = roundHours(total - target);

    if (target <= 0) {
        return { total, target, delta, state: 'unknown', ratio: 0 };
    }

    const state: HoursState = Math.abs(delta) <= HOURS_TOLERANCE ? 'ok' : delta > 0 ? 'over' : 'under';
    return { total, target, delta, state, ratio: Math.min(1, Math.max(0, total / target)) };
}

/** The gap message, or null when there is nothing to report. */
export function describeHoursDiscrepancy(summary: HoursSummary): string | null {
    if (summary.state === 'ok' || summary.state === 'unknown') return null;
    const key = summary.state === 'over' ? 'parcours.presence.hoursDiscrepancyOver' : 'parcours.presence.hoursDiscrepancyUnder';
    return translate(key, {
        entered: formatHours(summary.total),
        target: formatHours(summary.target),
        delta: formatHours(Math.abs(summary.delta)),
    });
}

/**
 * Spreads `target` evenly over every row, **absorbing the remainder on the last one**.
 *
 * The old version rounded each row independently: 3.75 h over 2 modules gave 1.88 × 2 = 3.76 h,
 * and 7.5 h over 7 gave 7.49 h. The total drifted by the sole fact of the tool (§1.6). Here the
 * sum is exactly `target`, up to the storage rounding.
 *
 * Overwrites every row without distinction: it is an explicit user gesture (the "Répartir
 * également" button), not a side effect of adding a row.
 */
export function distributeEvenly(rows: HoursBearingRow[], target: number): void {
    if (rows.length === 0) return;

    const share = roundHours(target / rows.length);
    rows.forEach((row, index) => {
        row.effectiveHours = index === rows.length - 1
            ? roundHours(target - share * (rows.length - 1))
            : share;
    });
}

/**
 * Can we still spread automatically without destroying anything?
 *
 * Yes as long as no row has been adjusted by hand and none comes from a recorded presence. That
 * preserves the comfort of the common case — open an empty cell, add a second module, get
 * 3.75 / 3.75 — while keeping the promise of §1.4: the user's work, and the hours actually
 * stored, are never silently rewritten.
 */
export function canAutoDistribute(rows: readonly HoursBearingRow[]): boolean {
    return rows.every(row => !row.touched && !row.presenceId);
}

/** What is left to allocate to reach the target, never negative. */
export function remainingHours(rows: readonly HoursBearingRow[], target: number): number {
    const used = rows.reduce((sum, row) => sum + (Number(row.effectiveHours) || 0), 0);
    return roundHours(Math.max(0, target - used));
}

/**
 * Aligns the still machine-made rows on a target that has just changed — typically late minutes
 * recorded after the fact. As long as nothing has been touched nor loaded from the database, the
 * durations are a default of the tool, not the user's work: following them keeps the sum right
 * with no further gesture (the typical case: one module, one late arrival, save).
 * As soon as a row is a fact (§1.4), we touch nothing — the gap is displayed, that is all.
 *
 * Returns true when the rows were aligned.
 */
export function syncAutoHours(rows: HoursBearingRow[], target: number): boolean {
    if (rows.length === 0 || target < 0 || !canAutoDistribute(rows)) return false;
    distributeEvenly(rows, target);
    return true;
}
